import {
  useLegalClient,
  useLegalClientSnapshot,
  useLegalClientVersions,
} from 'api/requests';
import { LegalClientVersionEntity } from 'api/types/entity';
import { NavigationBreadcrumbsTitles } from 'components';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, usePageTitle, RoutesId } from 'libs/navigation';

import { getLegalClientInitialValue } from '../helpers/getLegalClientInitialValue/getLegalClientInitialValue';

type ClientVersionContainerParams = {
  legalClientId: string;
  versionId: string;
};

export const useLegalClientVersion = () => {
  const { t } = useTranslation();

  const { params, navigate } = useNavigate<ClientVersionContainerParams>();

  const { legalClient, loading: clientLoading } = useLegalClient(
    params.legalClientId!
  );

  const {
    snapshot,
    loading: snapshotLoading,
    error,
  } = useLegalClientSnapshot(params.versionId!, { fetchPolicy: 'no-cache' });

  const { loading: versionsLoading, versions } = useLegalClientVersions(
    params.legalClientId!
  );

  const hasRepresentative = !!snapshot?.value?.representativeId;

  const initialValues = snapshot?.value
    ? getLegalClientInitialValue(t, snapshot.value)
    : undefined;

  const fullName = legalClient?.fullName!;

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.legalClient]: legalClient?.fullName,
    [RoutesId.legalClientVersion]: t('client.client.versions.header', {
      name: fullName,
    }),
  };

  const loading =
    (!legalClient && clientLoading) || (!versions && versionsLoading);

  const onClickRepresentative = () => {
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientRepresentativeVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        legalClientRepresentativeId: legalClient?.representativeId!,
        versionId: params.versionId!,
      }
    );
  };

  const onCloseVersionHistory = () => {
    navigate(ROUTES.clients.legalClients.legalClient.fullPath, {
      legalClientId: params.legalClientId!,
    });
  };

  const onChangeVersion = (version: LegalClientVersionEntity) => {
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        versionId: version.id,
      }
    );
  };

  const onClickHead = (headId: string) =>
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientHeadVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        versionId: params.versionId!,
        headId,
      }
    );

  const onClickLegalHead = (headId: string) =>
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientLegalHeadVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        versionId: params.versionId!,
        headId,
      }
    );

  const onClickBeneficiary = (beneficiaryId: string) =>
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientBeneficiaryVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        versionId: params.versionId!,
        beneficiaryId,
      }
    );

  const onClickBranch = (branchId: string) =>
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientBranchVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        versionId: params.versionId!,
        branchId,
      }
    );

  usePageTitle(fullName);

  return {
    versionId: params.versionId!,
    snapshot,
    loading,
    snapshotLoading: !snapshot && snapshotLoading,
    error,
    hasRepresentative,
    versions,
    fullName,
    initialValues,
    breadcrumbsTitles,
    legalClient,
    params,
    onClickHead,
    onClickLegalHead,
    onClickBeneficiary,
    onClickBranch,
    onChangeVersion,
    onCloseVersionHistory,
    onClickRepresentative,
  };
};
